import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  MapPin, Briefcase, Clock, Building2, ArrowLeft, Upload, 
  CheckCircle, AlertCircle, Loader2, DollarSign, Users 
} from 'lucide-react';
import axios from 'axios';
import Turnstile from 'react-turnstile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitMessage, setSubmitMessage] = useState('');
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    country: '',
    current_title: '',
    years_experience: '',
    expected_salary: '',
    work_authorization: '',
    linkedin_url: '',
    cover_letter: '',
    resume: null
  });
  
  // Turnstile CAPTCHA state
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  
  // Cloudflare Turnstile site key (use test key for development)
  // For production, replace with your own site key from Cloudflare dashboard
  const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

  useEffect(() => {
    fetchJob();
  }, [jobId]);

  const fetchJob = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/careers/jobs/${jobId}`);
      setJob(response.data);
    } catch (error) {
      console.error('Failed to fetch job:', error);
      if (error.response?.status === 404) {
        navigate('/careers');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['.pdf', '.doc', '.docx'];
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(ext)) {
        setErrors(prev => ({ ...prev, resume: 'Please upload a PDF, DOC, or DOCX file' }));
        return;
      }
      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, resume: 'File size must be less than 10MB' }));
        return;
      }
      setFormData(prev => ({ ...prev, resume: file }));
      setErrors(prev => ({ ...prev, resume: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!turnstileToken) {
      newErrors.turnstile = 'Please complete the security verification';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new FormData();
      
      // Add required fields
      formDataToSend.append('first_name', formData.first_name);
      formDataToSend.append('last_name', formData.last_name);
      formDataToSend.append('email', formData.email);
      
      // Add optional fields if provided
      if (formData.phone) formDataToSend.append('phone', formData.phone);
      if (formData.city) formDataToSend.append('city', formData.city);
      if (formData.state) formDataToSend.append('state', formData.state);
      if (formData.country) formDataToSend.append('country', formData.country);
      if (formData.current_title) formDataToSend.append('current_title', formData.current_title);
      if (formData.years_experience) formDataToSend.append('years_experience', formData.years_experience);
      if (formData.expected_salary) formDataToSend.append('expected_salary', formData.expected_salary);
      if (formData.work_authorization) formDataToSend.append('work_authorization', formData.work_authorization);
      if (formData.linkedin_url) formDataToSend.append('linkedin_url', formData.linkedin_url);
      if (formData.cover_letter) formDataToSend.append('cover_letter', formData.cover_letter);
      if (formData.resume) formDataToSend.append('resume', formData.resume);
      
      // Add Turnstile token for verification
      if (turnstileToken) formDataToSend.append('turnstile_token', turnstileToken);

      const response = await axios.post(
        `${API}/careers/apply/${jobId}`,
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSubmitStatus('success');
      setSubmitMessage(response.data.message || 'Application submitted successfully!');
      
    } catch (error) {
      console.error('Application submission failed:', error);
      setSubmitStatus('error');
      setSubmitMessage(
        error.response?.data?.detail || 
        'Failed to submit application. Please try again.'
      );
      // Reset Turnstile on error
      setTurnstileToken('');
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatWorkMode = (mode) => {
    const modes = { 'remote': 'Remote', 'hybrid': 'Hybrid', 'onsite': 'On-site' };
    return modes[mode] || mode;
  };

  const formatJobType = (type) => {
    const types = { 'fulltime': 'Full-time', 'contract': 'Contract', 'c2c': 'C2C' };
    return types[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-[#F58B23] border-t-transparent rounded-full animate-spin" />
          <p className="mt-4 text-[#6B7280]">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif font-bold text-[#1A1A1A] mb-4">Job Not Found</h2>
          <Link to="/careers">
            <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white">
              View All Jobs
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Success State
  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen pt-20">
        <section className="py-32 bg-gradient-to-br from-[#FFF4E6] to-[#FAFAF8]">
          <div className="container-custom">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl font-serif font-bold text-[#1A1A1A] mb-4">
                Application Submitted!
              </h1>
              <p className="text-[#6B7280] mb-8">
                Thank you for applying for the <strong>{job.title}</strong> position. 
                We've received your application and will review it shortly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/careers">
                  <Button variant="outline">
                    View More Jobs
                  </Button>
                </Link>
                <Link to="/">
                  <Button className="bg-[#F58B23] hover:bg-[#D97706] text-white">
                    Return Home
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-12 bg-gradient-to-br from-[#FFF4E6] to-[#FAFAF8]">
        <div className="container-custom">
          <Link
            to="/careers"
            className="inline-flex items-center text-[#F58B23] font-medium text-sm mb-6 hover:underline"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to All Jobs
          </Link>
          
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#1A1A1A] mb-4">
            {job.title}
          </h1>
          
          <div className="flex flex-wrap gap-4 text-[#6B7280] mb-6">
            {job.department && (
              <span className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full text-sm">
                <Building2 className="w-4 h-4 text-[#F58B23]" />
                {job.department}
              </span>
            )}
            {(job.city || job.location) && (
              <span className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full text-sm">
                <MapPin className="w-4 h-4 text-[#F58B23]" />
                {job.city || job.location}{job.state ? `, ${job.state}` : ''}
              </span>
            )}
            {job.work_mode && (
              <span className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full text-sm">
                <Clock className="w-4 h-4 text-[#F58B23]" />
                {formatWorkMode(job.work_mode)}
              </span>
            )}
            {job.job_type && (
              <span className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full text-sm">
                <Briefcase className="w-4 h-4 text-[#F58B23]" />
                {formatJobType(job.job_type)}
              </span>
            )}
            {job.openings > 1 && (
              <span className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-full text-sm">
                <Users className="w-4 h-4 text-[#F58B23]" />
                {job.openings} Openings
              </span>
            )}
          </div>

          {(job.salary_min > 0 || job.salary_max > 0) && (
            <div className="flex items-center gap-2 text-[#1A1A1A] font-medium">
              <DollarSign className="w-5 h-5 text-[#F58B23]" />
              {job.salary_min > 0 && job.salary_max > 0 
                ? `$${job.salary_min.toLocaleString()} - $${job.salary_max.toLocaleString()}`
                : job.salary_max > 0 
                  ? `Up to $${job.salary_max.toLocaleString()}`
                  : `From $${job.salary_min.toLocaleString()}`
              }
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Job Description */}
            <div className="lg:col-span-2">
              <div className="prose prose-gray max-w-none">
                <h2 className="text-2xl font-serif font-semibold text-[#1A1A1A] mb-6">
                  About This Role
                </h2>
                <div 
                  className="text-[#6B7280] leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: job.description?.replace(/\n/g, '<br/>') }}
                />
              </div>

              {job.required_skills && job.required_skills.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xl font-serif font-semibold text-[#1A1A1A] mb-4">
                    Required Skills
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {job.required_skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-[#FFF4E6] text-[#F58B23] rounded-full text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Apply Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-28 bg-[#FAFAF8] rounded-2xl p-6 border border-gray-100">
                {!showApplyForm ? (
                  <>
                    <h3 className="text-xl font-serif font-semibold text-[#1A1A1A] mb-4">
                      Interested?
                    </h3>
                    <p className="text-[#6B7280] text-sm mb-6">
                      Apply now and take the next step in your career journey with TekClap.
                    </p>
                    <Button
                      onClick={() => setShowApplyForm(true)}
                      className="w-full bg-[#F58B23] hover:bg-[#D97706] text-white py-6"
                    >
                      Apply for This Position
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-xl font-serif font-semibold text-[#1A1A1A]">
                        Apply Now
                      </h3>
                      <button
                        onClick={() => setShowApplyForm(false)}
                        className="text-sm text-[#6B7280] hover:text-[#F58B23]"
                      >
                        Cancel
                      </button>
                    </div>

                    {submitStatus === 'error' && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{submitMessage}</p>
                      </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                      {/* Name Fields */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="first_name" className="text-xs">
                            First Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="first_name"
                            value={formData.first_name}
                            onChange={(e) => handleInputChange('first_name', e.target.value)}
                            className={errors.first_name ? 'border-red-500' : ''}
                          />
                          {errors.first_name && (
                            <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
                          )}
                        </div>
                        <div>
                          <Label htmlFor="last_name" className="text-xs">
                            Last Name <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            id="last_name"
                            value={formData.last_name}
                            onChange={(e) => handleInputChange('last_name', e.target.value)}
                            className={errors.last_name ? 'border-red-500' : ''}
                          />
                          {errors.last_name && (
                            <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
                          )}
                        </div>
                      </div>

                      {/* Email */}
                      <div>
                        <Label htmlFor="email" className="text-xs">
                          Email <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className={errors.email ? 'border-red-500' : ''}
                        />
                        {errors.email && (
                          <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                        )}
                      </div>

                      {/* Phone */}
                      <div>
                        <Label htmlFor="phone" className="text-xs">Phone</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                        />
                      </div>

                      {/* Location */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="city" className="text-xs">City</Label>
                          <Input
                            id="city"
                            value={formData.city}
                            onChange={(e) => handleInputChange('city', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="state" className="text-xs">State</Label>
                          <Input
                            id="state"
                            value={formData.state}
                            onChange={(e) => handleInputChange('state', e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Current Title */}
                      <div>
                        <Label htmlFor="current_title" className="text-xs">Current Title</Label>
                        <Input
                          id="current_title"
                          value={formData.current_title}
                          onChange={(e) => handleInputChange('current_title', e.target.value)}
                        />
                      </div>

                      {/* Experience */}
                      <div>
                        <Label htmlFor="years_experience" className="text-xs">Years of Experience</Label>
                        <Select
                          value={formData.years_experience}
                          onValueChange={(value) => handleInputChange('years_experience', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0-2">0-2 years</SelectItem>
                            <SelectItem value="3-5">3-5 years</SelectItem>
                            <SelectItem value="6-10">6-10 years</SelectItem>
                            <SelectItem value="10+">10+ years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Work Authorization */}
                      <div>
                        <Label htmlFor="work_authorization" className="text-xs">Work Authorization</Label>
                        <Select
                          value={formData.work_authorization}
                          onValueChange={(value) => handleInputChange('work_authorization', value)}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="US Citizen">US Citizen</SelectItem>
                            <SelectItem value="Green Card">Green Card</SelectItem>
                            <SelectItem value="H1B">H1B Visa</SelectItem>
                            <SelectItem value="OPT/CPT">OPT/CPT</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* LinkedIn */}
                      <div>
                        <Label htmlFor="linkedin_url" className="text-xs">LinkedIn Profile</Label>
                        <Input
                          id="linkedin_url"
                          type="url"
                          placeholder="https://linkedin.com/in/..."
                          value={formData.linkedin_url}
                          onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                        />
                      </div>

                      {/* Resume Upload */}
                      <div>
                        <Label htmlFor="resume" className="text-xs">Resume</Label>
                        <div className="mt-1">
                          <label
                            htmlFor="resume"
                            className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                              formData.resume 
                                ? 'border-[#F58B23] bg-[#FFF4E6]' 
                                : 'border-gray-300 hover:border-[#F58B23]'
                            } ${errors.resume ? 'border-red-500' : ''}`}
                          >
                            <Upload className="w-5 h-5 text-[#6B7280]" />
                            <span className="text-sm text-[#6B7280]">
                              {formData.resume ? formData.resume.name : 'Upload PDF, DOC, DOCX'}
                            </span>
                          </label>
                          <input
                            id="resume"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                            className="hidden"
                          />
                          {errors.resume && (
                            <p className="text-red-500 text-xs mt-1">{errors.resume}</p>
                          )}
                        </div>
                      </div>

                      {/* Cover Letter */}
                      <div>
                        <Label htmlFor="cover_letter" className="text-xs">Cover Letter</Label>
                        <Textarea
                          id="cover_letter"
                          rows={3}
                          placeholder="Tell us why you're interested..."
                          value={formData.cover_letter}
                          onChange={(e) => handleInputChange('cover_letter', e.target.value)}
                        />
                      </div>

                      {/* Turnstile CAPTCHA */}
                      <div className="flex flex-col items-center">
                        <Turnstile
                          ref={turnstileRef}
                          sitekey={TURNSTILE_SITE_KEY}
                          onVerify={(token) => {
                            setTurnstileToken(token);
                            if (errors.turnstile) {
                              setErrors(prev => ({ ...prev, turnstile: null }));
                            }
                          }}
                          onExpire={() => setTurnstileToken('')}
                          onError={() => setTurnstileToken('')}
                          theme="light"
                          size="normal"
                        />
                        {errors.turnstile && (
                          <p className="text-red-500 text-xs mt-2">{errors.turnstile}</p>
                        )}
                      </div>

                      {/* Submit Button */}
                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-[#F58B23] hover:bg-[#D97706] text-white py-6"
                      >
                        {submitting ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Submitting...
                          </span>
                        ) : (
                          'Submit Application'
                        )}
                      </Button>

                      <p className="text-xs text-[#9CA3AF] text-center">
                        By submitting, you agree to our{' '}
                        <Link to="/privacy-policy" className="text-[#F58B23] hover:underline">
                          Privacy Policy
                        </Link>
                      </p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobDetailPage;
